/**
 * As seções da tela da implantação no console e o vocabulário que liga esteira, cards e linha
 * do tempo a elas (SUS-15400).
 *
 * A seção aberta mora no hash da URL (`#kickoff`): é o que permite mandar o link direto de uma
 * seção e o que o botão Voltar do navegador desfaz. Hash desconhecido, ou de uma seção que a
 * sessão não alcança, abre Contatos de acesso — nunca uma tela vazia.
 */

import type { EventoDaImplantacao } from "./console";
import type { Etapa } from "./implantacoes";

export const SECOES_DA_IMPLANTACAO = [
  { id: "contatos", titulo: "Contatos de acesso" },
  { id: "kickoff", titulo: "Kickoff" },
  { id: "reunioes", titulo: "Reuniões de implantação" },
  { id: "repasse", titulo: "Módulos e repasse" },
  { id: "credenciais", titulo: "Credenciais" },
  { id: "contrato", titulo: "Contrato e escopo" },
  { id: "linha", titulo: "Linha do tempo" },
] as const;

export type SecaoDaImplantacao = (typeof SECOES_DA_IMPLANTACAO)[number]["id"];

export const SECAO_PADRAO: SecaoDaImplantacao = "contatos";

export function secaoDoHash(
  hash: string,
  disponiveis: readonly SecaoDaImplantacao[] = SECOES_DA_IMPLANTACAO.map((secao) => secao.id),
): SecaoDaImplantacao {
  const id = hash.replace(/^#/, "");
  return (disponiveis as readonly string[]).includes(id) ? (id as SecaoDaImplantacao) : SECAO_PADRAO;
}

/* Os botões da esteira abrem o card que responde pela etapa, em vez de levar a outra tela:
   infraestrutura e kickoff são respostas do cliente, a reunião é agenda e ata, e a execução é o
   andamento dos módulos. Contrato não tem atalho: a etapa já está registrada no cabeçalho. */
export const ATALHO_DA_ETAPA: Partial<
  Record<Etapa, { secao: SecaoDaImplantacao; rotulo: string }>
> = {
  infraestrutura: { secao: "kickoff", rotulo: "Ver respostas" },
  kickoff: { secao: "kickoff", rotulo: "Ver respostas" },
  reuniao: { secao: "reunioes", rotulo: "Ver reunião" },
  execucao: { secao: "repasse", rotulo: "Ver módulos" },
};

export const CATEGORIAS_DA_LINHA_DO_TEMPO = [
  "Tudo",
  "Esteira",
  "Kickoff",
  "Reuniões",
  "Acessos",
  "Repasse",
] as const;

export type CategoriaDoEvento = Exclude<(typeof CATEGORIAS_DA_LINHA_DO_TEMPO)[number], "Tudo">;

const CATEGORIA_DO_TIPO: Record<string, CategoriaDoEvento> = {
  implantacao_criada: "Esteira",
  infra_iniciada: "Esteira",
  infra_respondida: "Esteira",
  infra_devolvida: "Esteira",
  ambiente_liberado: "Esteira",
  execucao_iniciada: "Esteira",
  implantacao_concluida: "Esteira",
  kickoff_iniciado: "Kickoff",
  kickoff_enviado: "Kickoff",
  pendencia_aberta: "Kickoff",
  pendencia_resolvida: "Kickoff",
  reuniao_agendada: "Reuniões",
  reuniao_realizada: "Reuniões",
  ata_publicada: "Reuniões",
  contato_convidado: "Acessos",
};

/* A observação carrega o assunto no detalhe: as do kickoff e da reunião vão para a categoria
   delas, as do repasse para Repasse, e o resto fica na esteira — o filtro nunca esconde um
   evento de todas as categorias. */
export function categoriaDoEvento(evento: Pick<EventoDaImplantacao, "tipo" | "detalhe">): CategoriaDoEvento {
  const doTipo = CATEGORIA_DO_TIPO[evento.tipo];
  if (doTipo) return doTipo;
  const assunto = typeof evento.detalhe.assunto === "string" ? evento.detalhe.assunto : "";
  if (assunto.startsWith("kickoff")) return "Kickoff";
  if (assunto.startsWith("reuniao")) return "Reuniões";
  if (assunto.includes("repasse") || "modulo" in evento.detalhe) return "Repasse";
  return "Esteira";
}

/* ---------------------------------------------------------------- resumos dos cards */

/** "1 trilha", "2 trilhas": a contagem com a palavra no número certo. */
export function plural(quantidade: number, singular: string, varios: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : varios}`;
}

/* O percentual de cada trilha vem pronto do servidor, que mede as obrigatórias. O card só tira a
   média simples delas: uma trilha de 4 perguntas pesa o mesmo que uma de 40, como no quadro das
   trilhas, em que cada linha é uma trilha. */
export function resumoDoKickoff(percentuais: readonly number[]): string {
  const trilhas = plural(percentuais.length, "trilha", "trilhas");
  if (percentuais.length === 0) return trilhas;
  const media = Math.round(percentuais.reduce((soma, valor) => soma + valor, 0) / percentuais.length);
  return `${trilhas} · ${media}% respondido`;
}

/* "Em execução" é o módulo que nem foi repassado nem rejeitado no Jira: o que a equipe ainda
   está configurando. O rejeitado só aparece quando existe, para o resumo não falar de zero. */
export function resumoDoRepasse(
  modulos: readonly { repassado: unknown; rejeitado: boolean }[],
): string {
  const repassados = modulos.filter((modulo) => modulo.repassado).length;
  const rejeitados = modulos.filter((modulo) => !modulo.repassado && modulo.rejeitado).length;
  const partes = [
    plural(repassados, "repassado", "repassados"),
    `${modulos.length - repassados - rejeitados} em execução`,
  ];
  if (rejeitados > 0) partes.push(plural(rejeitados, "rejeitado", "rejeitados"));
  return partes.join(" · ");
}
